import { Environment, MediaConstants, PlayerConstants } from '@accedo/xdk-core'
import XdkPlayer from '@accedo/xdk-local-modules/src/XdkPlayer'
import { Registry, Settings } from '@lightningjs/sdk'

import { Router } from '@lightningjs/sdk'
import { PLAYBACK_SIGNALS } from '../../constants/signals'
import { CONTENT_PLAYING_TIME_INTERVAL, PLAYER_EVENTS as EVENTS } from '../../constants/videoPlayer'
import { AppConfig } from '../../services/appConfig'
import { Loader } from '../Loader/LoaderWrapper'
import SpeechComp from '../speechComp/speechComp'
import VideoControl, { VideoControlStates } from './videoControl'

const environment = Environment.singleton()

const { STATE_CHANGED, TIME_UPDATE, BUFFERING, FINISHED, ERROR } = environment.MEDIA
const {
  STATE: { CONNECTING: CONNECTING_STATE },
} = MediaConstants
const {
  BUFFERING: { START: BUFFERING_START },
} = PlayerConstants

const LIVE_EDGE_THRESHOLD = 60

export default class VideoPlayer extends SpeechComp {
  private _stretched: boolean
  private _videoEnd: boolean
  private _onStateChanged: Function
  private _onTimeUpdated: Function
  private _onBuffering: Function
  private _onFinished: Function
  private _onError: Function
  private _contentPlayingEventTimer: number
  private _subtitleVisibilityCheck = false /** indicates if we should set subtitles. */
  private _isLiveStream: boolean

  static _template() {
    return {
      w: 1920,
      h: 1080,
      collision: true,
      Player: {
        type: XdkPlayer,
      },
      Spinner: {
        type: Loader,
        x: (w) => w / 2,
        y: (h) => h / 2,
        mount: 0.5,
        width: 112,
        height: 103,
        visible: false,
        zIndex: 99,
      },
      Controls: {
        type: VideoControl,
        signals: PLAYBACK_SIGNALS,
        passSignals: PLAYBACK_SIGNALS,
      },
    }
  }

  resetFlags() {
    this._stretched = false
    this._videoEnd = false
    this._contentPlayingEventTimer = 0
  }

  async _enable() {
    this.resetFlags()
    this._onStateChanged = this.onStateChanged.bind(this)
    this._onTimeUpdated = this.onTimeUpdate.bind(this)
    this._onBuffering = this.onBuffering.bind(this)
    this._onFinished = this.onFinished.bind(this)
    this._onError = this.onError.bind(this)

    Registry.addEventListener(environment as any, STATE_CHANGED, this._onStateChanged)
    Registry.addEventListener(environment as any, TIME_UPDATE, this._onTimeUpdated)
    Registry.addEventListener(environment as any, BUFFERING, this._onBuffering)
    Registry.addEventListener(environment as any, FINISHED, this._onFinished)
    Registry.addEventListener(environment as any, ERROR, this._onError)
    this.speak()
  }

  _disable() {
    Registry.removeEventListener(environment as any, STATE_CHANGED, this._onStateChanged)
    Registry.removeEventListener(environment as any, TIME_UPDATE, this._onTimeUpdated)
    Registry.removeEventListener(environment as any, BUFFERING, this._onBuffering)
    Registry.removeEventListener(environment as any, FINISHED, this._onFinished)
    Registry.removeEventListener(environment as any, ERROR, this._onError)

    this._onStateChanged = null
    this._onTimeUpdated = null
    this._onBuffering = null
    this._onFinished = null
    this._onError = null
    this.resetFlags()

    if (this.controls.playerState) {
      this.player.pause()
    }
  }

  _getFocused() {
    return this.controls
  }

  get videoElement() {
    return this.player?.videoElement
  }

  get volume() {
    return Math.round(this.player?.volume * 100) || -1
  }

  get spinner() {
    return this.tag('Spinner') as Loader
  }

  get sectionText() {
    return this.controls.title.text?.text + ' . . ' + this.controls.subtitle.text?.text
  }

  get speechText() {
    return 'Press Any Button To Enable Video Controls'
  }

  get controls() {
    return this.tag('Controls') as VideoControl
  }

  get player() {
    return this.tag('Player') as XdkPlayer
  }

  get jumpSecond() {
    return Settings.get('app', 'video.jumpSecond', 10)
  }

  set isLiveStream(v) {
    this._isLiveStream = v
    this.controls.isLiveStream = v
  }

  get isLiveStream() {
    return this._isLiveStream
  }

  set authDeeplinked(v) {
    this.controls.authDeeplinked = v
  }

  set contentPlayingTimer(initialSeek) {
    this._contentPlayingEventTimer = initialSeek
  }

  onStateChanged({ from, to }) {
    console.debug(`${from} -> ${to}`)

    this.controls.playerState = to

    if (to === CONNECTING_STATE && !this._stretched) {
      this._stretched = true
      this.player.fullscreen()
    }
  }

  onTimeUpdate({ time, duration }) {
    this.controls.currentTime = time
    if (this._isLiveStream) {
      const video = this.player.videoElement
      if (video.seekable.length > 0) {
        const liveEdge = video.seekable.end(video.seekable.length - 1)
        this.controls.duration = liveEdge
        this.controls.timeLimitText = liveEdge - time
        this.controls.ranges.atLiveEdge = liveEdge - time < LIVE_EDGE_THRESHOLD
      }
    } else {
      this.controls.duration = duration
      this.controls.timeLimitText = duration - time
      this.controls.currentTime = time
    }

    // preload trickplay thumbnail info
    this.controls.preloadScrubTrickplay(time)

    if (this._subtitleVisibilityCheck) {
      this._subtitleVisibilityCheck = false
      AppConfig.shared.appSettings.isCcOn ? this.showSubtitles() : this.hideSubtitles()
    }

    this.signal(PLAYBACK_SIGNALS.updateVideoAnalytics, {
      contentPosition: time,
      contentDuration: duration,
      playing: this.controls.playerState,
    })

    this.signal(PLAYBACK_SIGNALS.updateHeartbeat, {
      position: time,
      duration,
    })

    const timerDifference = time - this._contentPlayingEventTimer

    if (
      timerDifference >= CONTENT_PLAYING_TIME_INTERVAL &&
      timerDifference < CONTENT_PLAYING_TIME_INTERVAL + 1
    ) {
      this._contentPlayingEventTimer = Math.floor(time)
      this.signal(PLAYBACK_SIGNALS.reportContentEvent, EVENTS.PLAYING)
    }
  }

  onBuffering({ type }) {
    if (!this._videoEnd) {
      this.spinner.visible = type === BUFFERING_START
    }
  }

  onFinished() {
    this._videoEnd = true
    this.signal(PLAYBACK_SIGNALS.videoEnd)
  }

  onError(data) {
    this.signal(PLAYBACK_SIGNALS.videoError, data)
    this.signal(PLAYBACK_SIGNALS.reportPlaybackEvent, EVENTS.INTERRUPTED)
  }

  async open(src) {
    this.hideSubtitles()
    await this.player.open(src, { seek: 'end' })

    this.signal(PLAYBACK_SIGNALS.videoStart)
    this._subtitleVisibilityCheck = true
  }

  pause() {
    this.spinner.visible = false
    this.player.pause()

    const { currentTime, duration } = this.player
    this.signal('saveToCW', currentTime, duration)
  }

  play(seconds = null) {
    this.spinner.visible = false
    this.player.play(seconds)
    this._subtitleVisibilityCheck = true
  }

  stop() {
    this.signal(PLAYBACK_SIGNALS.stop)
  }

  restart() {
    this.player.reload()

    // Reset subtitles
    this._subtitleVisibilityCheck = true
  }

  jumpBackward() {
    this._contentPlayingEventTimer -= this.jumpSecond
    this.signal(
      PLAYBACK_SIGNALS.reportPlaybackEvent,
      EVENTS.SEEK_COMPLETED,
      this._contentPlayingEventTimer,
    )

    const skipTo = this.controls.currentTime - this.jumpSecond
    this.player.seek(skipTo)
  }

  jumpForward() {
    this._contentPlayingEventTimer += this.jumpSecond
    this.signal(
      PLAYBACK_SIGNALS.reportPlaybackEvent,
      EVENTS.SEEK_COMPLETED,
      this._contentPlayingEventTimer,
    )

    const skipTo = this.controls.currentTime + this.jumpSecond
    this.player.seek(skipTo)
  }

  seek(to) {
    this._contentPlayingEventTimer = to
    this.signal(PLAYBACK_SIGNALS.reportPlaybackEvent, EVENTS.SEEK_COMPLETED, Math.round(to))

    this.player.seek(to)
  }

  close() {
    this.player.close()
  }

  showSubtitles() {
    if (this.videoElement?.textTracks?.length > 0) {
      this.videoElement.textTracks[0].mode = 'showing'
    }
  }

  hideSubtitles() {
    if (this.videoElement?.textTracks?.length > 0) {
      this.videoElement.textTracks[0].mode = 'disabled'
    }
  }

  async reset() {
    this.resetFlags()
    await this.player.reset()
  }

  _handleClick() {
    const widgets = Router.getActivePage().widgets
    const isModalOpen = Object.values(widgets).some((widget: any) => {
      return widget.ref !== 'Menu' && widget.ref !== 'Loading' && widget.visible
    })

    if (
      this.controls.isScrubMode ||
      this.controls.isScrubbing ||
      this.controls.state === VideoControlStates.Carousel ||
      isModalOpen
    ) {
      return
    }

    this.controls.controlButtons.playPause()
  }
}
